import { Box, Divider, Typography } from '@mui/material';
import { ReactNode } from 'react';
import { FormattedMessage, MessageDescriptor } from 'react-intl';

interface ContentClusterProps {
  /** Intl message for the group title. Prefer this when the copy is extractable. */
  message?: MessageDescriptor;
  /** Optional values interpolated into `message` (e.g. count). */
  values?: Record<string, string | number>;
  /** Pre-rendered title when `message` is omitted. */
  title?: ReactNode;
  /**
   * Optional muted description under the title. When provided, the top hairline
   * is off by default (description OR divider — not both unless `divider` is set).
   */
  description?: ReactNode;
  /**
   * Top hairline separating this group from the previous one.
   * Defaults to true when there is no description; false when description is set.
   */
  divider?: boolean;
  /**
   * Neutral 2px left ribbon for status meaning (error, optional, AI-filled).
   * Do not use for visual grouping alone.
   */
  accent?: boolean;
  children: ReactNode;
}

/**
 * Groups related short content into a scannable cluster (editable or read-only).
 * Separates clusters with a hairline or a description — no card around the label.
 * Body may be a headerless Panel for interactive row lists (see content-structure README).
 */
export function ContentCluster({
  message,
  values,
  title,
  description,
  divider,
  accent = false,
  children,
}: ContentClusterProps) {
  const showDivider = divider ?? !description;
  const resolvedTitle = message ? <FormattedMessage {...message} values={values} /> : title;

  return (
    <Box
      sx={{
        ...(accent && {
          borderLeft: 2,
          borderColor: 'text.secondary',
          pl: 1.5,
        }),
      }}
    >
      {showDivider && (
        <Divider
          sx={{
            // Space above so the hairline sits between groups, not flush on the last field border.
            mt: 4,
            mb: 3,
            borderColor: 'divider',
            borderBottomWidth: '0.5px',
          }}
        />
      )}

      {resolvedTitle && (
        <Typography
          variant="subtitle2"
          color="text.primary"
          sx={{
            fontWeight: 500,
            mb: description ? 1 : 2,
          }}
        >
          {resolvedTitle}
        </Typography>
      )}

      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          {description}
        </Typography>
      )}

      {children}
    </Box>
  );
}
